// Common types and utilities
//
// Partially schema-authoritative: FirebaseTimestamp is now a Zod schema with its
// type derived via z.infer<>. The generic shapes (PaginatedResponse<T>,
// CursorPaginatedResponse<T>, ApiResponse<T>) and the index-signature shapes
// (FilterParams, CursorFilterParams) stay as TS interfaces — Zod can't reproduce
// those exact types.

import { z } from 'zod';

/**
 * Standard paginated response format for all API endpoints
 * Uses snake_case naming convention to match backend standards
 *
 * @template T - The type of items in the data array
 * @example
 * ```typescript
 * const response: PaginatedResponse<User> = {
 *   data: [user1, user2],
 *   total_count: 100,
 *   page_number: 1,
 *   page_size: 20,
 *   total_pages: 5
 * };
 * ```
 */
export interface PaginatedResponse<T> {
  /** Array of items for the current page */
  data: T[];
  /** Total number of items across all pages */
  total_count: number;
  /** Current page number (1-based) */
  page_number: number;
  /** Number of items per page */
  page_size: number;
  /** Total number of pages */
  total_pages: number;
  /** When the response was created (ISO string) */
  createdAt?: string;
  /** When the response was last updated (ISO string) */
  updatedAt?: string;
}

/**
 * Standard filter and pagination parameters for API requests
 * Uses snake_case naming convention to match backend standards
 *
 * @example
 * ```typescript
 * const params: FilterParams = {
 *   page_number: 1,
 *   page_size: 20,
 *   sort_by: 'createdAt',
 *   sort_order: 'desc',
 *   search: 'medical'
 * };
 * ```
 */
export interface FilterParams {
  /** Page number to retrieve (1-based, defaults to 1) */
  page_number?: number;
  /** Number of items per page (defaults to 20, max 100) */
  page_size?: number;
  /** Field name to sort by */
  sort_by?: string;
  /** Sort direction */
  sort_order?: 'asc' | 'desc';
  /** Search query string */
  search?: string;
  /** Additional filter parameters */
  [key: string]: string | number | boolean | undefined;
}

/**
 * Cursor-based pagination parameters for large datasets
 * Provides better performance for infinite scroll scenarios
 */
export interface CursorFilterParams extends Omit<FilterParams, 'page_number'> {
  /** Cursor for pagination (opaque string) */
  cursor?: string;
  /** Number of items to retrieve (defaults to 20) */
  limit?: number;
}

/**
 * Cursor-based paginated response
 * @template T - The type of items in the data array
 */
export interface CursorPaginatedResponse<T> {
  /** Array of items for the current page */
  data: T[];
  /** Cursor for the next page (null if no more pages) */
  next_cursor: string | null;
  /** Cursor for the previous page (null if first page) */
  previous_cursor: string | null;
  /** Total count (may be approximate for performance) */
  total_count?: number;
  /** Whether there are more items available */
  has_more: boolean;
  /** When the response was created (ISO string) */
  createdAt?: string;
  /** When the response was last updated (ISO string) */
  updatedAt?: string;
}

// =============================================================================
// STANDARD API RESPONSE FORMATS
// =============================================================================

/**
 * Standard API response wrapper for all endpoints
 * @template T - The type of the response data
 */
export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
  createdAt?: string;
  updatedAt?: string;
}

// =============================================================================
// UTILITY TYPES
// =============================================================================

/** Helper type for Firestore Timestamps that come from the backend */
export const FirebaseTimestampSchema = z.object({
  seconds: z.number(),
  nanoseconds: z.number(),
});

export type FirebaseTimestamp = z.infer<typeof FirebaseTimestampSchema>;
